// Renders a certificate's HTML into a hidden iframe, rasterizes it and saves
// it as an A4 PDF via jspdf. The rasterizer of record is modern-screenshot's
// SVG foreignObject capture: the browser itself lays the page out, so text
// lands exactly where it does on screen. html2canvas is kept only as a
// fallback — it re-implements text layout with its own font metrics, and the
// certificate template is a Word replica whose overflow:hidden boxes have no
// vertical slack, so any metric drift (severe on Windows display scaling)
// clips the bottom off every table row. All libraries are dynamically
// imported so they stay out of the main bundle until a user actually
// downloads a certificate.

// A4 at 96dpi. The iframe is laid out at this width so the capture matches
// what the print window produces on paper.
const A4_WIDTH_PX = 794;
const A4_HEIGHT_PX = 1123;

const IMAGE_LOAD_TIMEOUT_MS = 10_000;

const waitForImages = async (doc: Document) => {
  const pending = Array.from(doc.images)
    .filter((img) => !img.complete)
    .map(
      (img) =>
        new Promise<void>((resolve) => {
          img.addEventListener('load', () => resolve(), { once: true });
          img.addEventListener('error', () => resolve(), { once: true });
        })
    );
  if (pending.length === 0) return;

  await Promise.race([
    Promise.all(pending),
    new Promise<void>((resolve) => setTimeout(resolve, IMAGE_LOAD_TIMEOUT_MS))
  ]);
};

export const normalizeInlineBlockTextForRaster = (doc: Document) => {
  const win = doc.defaultView;
  const body = doc.body;
  if (!win || !body) return;
  for (const element of Array.from(body.querySelectorAll<HTMLElement>('*'))) {
    const display = win.getComputedStyle(element).display;
    if (display !== 'inline-block' && display !== 'inline-table') continue;
    for (const child of Array.from(element.children) as HTMLElement[]) {
      if (!child.style || win.getComputedStyle(child).display !== 'block') continue;
      child.style.display = 'inline-block';
      if (!child.style.width) child.style.width = '100%';
    }
  }
};

// Capture the certificate page itself when the template provides one — it is
// sized exactly A4, so the PDF fills the sheet edge to edge instead of
// embedding the page inside the viewer chrome (grey backdrop + padding).
export const selectCaptureTarget = (doc: {
  querySelector(selector: string): Element | null;
  body: HTMLElement | null;
}): HTMLElement | null => (doc.querySelector('.certificate-page') as HTMLElement | null) ?? doc.body;

// Best-effort: an image that cannot be fetched (service outage, or a CSP
// connect-src gap like the api.qrserver.com one that silently broke every
// download) keeps its original src. It must NOT reject — the caller treats a
// throw as "foreignObject capture unusable" and drops to the html2canvas
// fallback, which corrupts the whole certificate on Windows display scaling.
// One un-inlined image at worst renders blank in the capture.
export const inlineImageElements = async (
  images: Iterable<{ src: string }>,
  fetchImageAsDataUrl: (src: string) => Promise<string>
): Promise<void> => {
  await Promise.all(
    Array.from(images).map(async (img) => {
      const src = String(img.src || '');
      if (!/^https?:/i.test(src)) return;
      try {
        img.src = await fetchImageAsDataUrl(src);
      } catch (error) {
        console.warn(`certificate download: could not inline image ${src}`, error);
      }
    })
  );
};

const fetchImageAsDataUrl = async (src: string): Promise<string> => {
  const response = await fetch(src, { mode: 'cors' });
  if (!response.ok) throw new Error(`image fetch failed: ${response.status}`);
  const blob = await response.blob();
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('image read failed'));
    reader.readAsDataURL(blob);
  });
};

const captureCertificateCanvas = async (doc: Document): Promise<HTMLCanvasElement | null> => {
  const target = selectCaptureTarget(doc);
  if (!target) return null;

  try {
    await inlineImageElements(Array.from(doc.images), fetchImageAsDataUrl);
    const { domToCanvas } = await import('modern-screenshot');
    const canvas = await domToCanvas(target, {
      scale: 2,
      backgroundColor: '#ffffff'
    });
    if (canvas.width && canvas.height) return canvas;
    console.warn('certificate download: foreignObject capture returned an empty canvas');
  } catch (error) {
    // Fall through to the legacy rasterizer — but never silently: on Windows
    // display scaling the fallback clips text out of the certificate, so the
    // reason the faithful path failed is the first thing to look for.
    console.warn('certificate download: foreignObject capture failed, using html2canvas', error);
  }

  // Works around an html2canvas quirk (text dropped inside inline-block
  // boxes). Must not run before the foreignObject capture — it mutates layout
  // the real engine would otherwise render faithfully.
  normalizeInlineBlockTextForRaster(doc);
  const { default: html2canvas } = await import('html2canvas');
  const canvas = await html2canvas(doc.body, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    windowWidth: A4_WIDTH_PX
  });
  return canvas.width && canvas.height ? canvas : null;
};

const MIN_SINGLE_PAGE_SCALE = 0.6;

type PagePlan =
  | { mode: 'single'; widthMm: number; heightMm: number; offsetXMm: number }
  | { mode: 'multi'; pageHeightPx: number };

export const planCertificatePages = ({
  canvasWidth,
  canvasHeight,
  pageWidthMm,
  pageHeightMm
}: {
  canvasWidth: number;
  canvasHeight: number;
  pageWidthMm: number;
  pageHeightMm: number;
}): PagePlan => {
  const pageHeightPx = Math.floor((canvasWidth * pageHeightMm) / pageWidthMm);
  if (canvasHeight <= pageHeightPx) {
    return {
      mode: 'single',
      widthMm: pageWidthMm,
      heightMm: (canvasHeight * pageWidthMm) / canvasWidth,
      offsetXMm: 0
    };
  }
  const fitScale = pageHeightPx / canvasHeight;
  if (fitScale >= MIN_SINGLE_PAGE_SCALE) {
    const widthMm = (canvasWidth / canvasHeight) * pageHeightMm;
    return { mode: 'single', widthMm, heightMm: pageHeightMm, offsetXMm: (pageWidthMm - widthMm) / 2 };
  }
  return { mode: 'multi', pageHeightPx };
};

const BLANK_CHANNEL_MIN = 250;

export const findCleanBreakRow = (
  pixels: { data: Uint8ClampedArray | number[]; width: number; height: number },
  targetY: number,
  minY: number
): number => {
  const { data, width } = pixels;
  const isBlankRow = (y: number) => {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      if (
        data[i] < BLANK_CHANNEL_MIN ||
        data[i + 1] < BLANK_CHANNEL_MIN ||
        data[i + 2] < BLANK_CHANNEL_MIN
      ) {
        return false;
      }
    }
    return true;
  };
  for (let y = targetY; y > minY; y--) {
    if (isBlankRow(y)) return y;
  }
  return targetY;
};

export const downloadCertificatePdf = async (
  html: string,
  suggestedFileName?: string
): Promise<boolean> => {
  if (!html) return false;

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.left = '-10000px';
  iframe.style.top = '0';
  iframe.style.width = `${A4_WIDTH_PX}px`;
  iframe.style.height = `${A4_HEIGHT_PX}px`;
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  try {
    const doc = iframe.contentDocument;
    if (!doc) return false;

    doc.open();
    doc.write(html);
    doc.close();
    const jspdfImport = import('jspdf');
    jspdfImport.catch(() => {});
    // The logo/signature/QR images are served from Supabase storage and
    // api.qrserver.com with permissive CORS, so both rasterizers can inline
    // them; waiting here ensures layout has settled before capture.
    await waitForImages(doc);

    const canvas = await captureCertificateCanvas(doc);
    if (!canvas) return false;

    const { jsPDF } = await jspdfImport;
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidthMm = pdf.internal.pageSize.getWidth();
    const pageHeightMm = pdf.internal.pageSize.getHeight();
    const plan = planCertificatePages({
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      pageWidthMm,
      pageHeightMm
    });

    if (plan.mode === 'single') {
      pdf.addImage(
        canvas.toDataURL('image/jpeg', 0.95),
        'JPEG',
        plan.offsetXMm,
        0,
        plan.widthMm,
        plan.heightMm
      );
    } else {
      const sourceCtx = canvas.getContext('2d');
      if (!sourceCtx) return false;
      const pixels = sourceCtx.getImageData(0, 0, canvas.width, canvas.height);
      const { pageHeightPx } = plan;

      let renderedHeight = 0;
      let pageIndex = 0;
      while (renderedHeight < canvas.height) {
        let sliceEnd = Math.min(renderedHeight + pageHeightPx, canvas.height);
        if (sliceEnd < canvas.height) {
          const minY = renderedHeight + Math.floor(pageHeightPx / 2);
          sliceEnd = findCleanBreakRow(pixels, sliceEnd, minY);
        }
        const sliceHeight = sliceEnd - renderedHeight;
        const slice = document.createElement('canvas');
        slice.width = canvas.width;
        slice.height = sliceHeight;
        const ctx = slice.getContext('2d');
        if (!ctx) return false;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, slice.width, slice.height);
        ctx.drawImage(
          canvas,
          0,
          renderedHeight,
          canvas.width,
          sliceHeight,
          0,
          0,
          canvas.width,
          sliceHeight
        );

        if (pageIndex > 0) pdf.addPage();
        pdf.addImage(
          slice.toDataURL('image/jpeg', 0.95),
          'JPEG',
          0,
          0,
          pageWidthMm,
          (sliceHeight * pageWidthMm) / canvas.width
        );

        renderedHeight = sliceEnd;
        pageIndex += 1;
      }
    }

    const fileName = String(suggestedFileName || 'certificate').trim() || 'certificate';
    pdf.save(`${fileName}.pdf`);
    return true;
  } catch {
    return false;
  } finally {
    iframe.remove();
  }
};
